import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  useContext,
  useCallback,
} from 'react';

import { useIntl } from 'react-intl';

import { ClusterMarker } from '@atoms/cluster-marker';

import { useDebounceCallback } from '@utils/use-debounce-effect';

import { MapMarker } from '@molecules/map-marker';

import { SearchContext } from '@modules/atlas/contexts/search';
import { Tooltip } from '@modules/atlas/molecules/map-marker/tooltip';

import { LocationMarker } from '@atlas/atoms/location-marker';
import { MapCursorWithText } from '@atlas/molecules/map-cursor-with-text';
import { ParcelHighlighter } from '@atlas/molecules/parcel-highlighter';
import { SelectedAddressMarker } from '@atlas/atoms/selected-address-marker/selected-address-marker';
import { AtlasHighlightMarker } from '@atlas/atoms/atlas-highlight-marker';
import { AtlasEventsContext } from '@atlas/contexts/events';
import Map from '@atlas/organisms/map/map';
import { DesktopTileTypeButton } from '@atlas/molecules/desktop-tile-type-button';

import { useAtlasMapData } from './atlas-map.hooks';

import classes from './atlas-map.module.scss';

export const AtlasMap = () => {
  // map data hook
  const { pointers, clusterPointers } = useAtlasMapData();
  const [overlayVisible, setOverlayVisible] = useState(false);

  const { messages } = useIntl();

  const { on, emit } = useContext(AtlasEventsContext);
  const { setSearch } = useContext(SearchContext);

  useEffect(() => {
    return on('mapOverlayChange', (visibility: boolean) => {
      setOverlayVisible(visibility);
    });
  }, []);

  const onOverlayClick = useCallback(() => {
    emit('mapOverlayClick', undefined);
  }, [emit]);

  const updateBodyHeight = useDebounceCallback(
    () => {
      document.body.style.height = '100.1%';
      setTimeout(() => {
        document.body.style.height = '100%';
      }, 100);
    },
    [],
    600,
  );

  // iphone rotate white space fix
  useEffect(() => {
    if (navigator.platform.match(/iPhone/)) {
      window.addEventListener('resize', updateBodyHeight);

      return () => {
        window.removeEventListener('resize', updateBodyHeight);
      };
    }
  }, [updateBodyHeight]);

  // pointers memo
  const pointersMap = useMemo(
    () =>
      pointers.map((pointer) => {
        const handleClick = () => {
          setSearch({
            text: pointer.address
              ? ''
              : `${messages['atlas.search.position_on_map']}`,
            point: [pointer.point.coordinates[1], pointer.point.coordinates[0]],
            address: pointer.address,
          });
        };

        const isFlatWithPricePerSquareM =
          pointer.averagePricePerSquareM && pointer.subType === 'flat';

        return (
          <MapMarker
            handleClick={handleClick}
            pointer={pointer}
            key={pointer.geohash}
          >
            {isFlatWithPricePerSquareM && <Tooltip pointer={pointer} />}
          </MapMarker>
        );
      }),
    [pointers, setSearch, messages],
  );

  // cluster pointers memo
  const clusterPointersMap = useMemo(
    () =>
      clusterPointers.map((clusterPointer, index) => (
        <ClusterMarker clusterPointer={clusterPointer} key={index} />
      )),
    [clusterPointers],
  );

  // map overlay
  const mapOverlay = useMemo(
    () => (
      <div className={classes.desktopTileButtons}>
        {/* TODO: move this into <FloatingControls /> */}
        <DesktopTileTypeButton />
      </div>
    ),
    [],
  );

  const wrapper = useRef(null);

  const renderedMap = useMemo(() => {
    return (
      <Map overlay={mapOverlay}>
        <AtlasHighlightMarker />
        <SelectedAddressMarker />
        <MapCursorWithText boundingElement={wrapper} />
        <ParcelHighlighter />
        <LocationMarker />

        {/* CLUSTER POINTERS */}
        {clusterPointersMap}

        {/* POINTERS */}
        {pointersMap}
      </Map>
    );
  }, [clusterPointersMap, pointersMap, mapOverlay]);

  return useMemo(
    () => (
      <div ref={wrapper} className={classes.wrapper}>
        {overlayVisible && (
          <div className={classes.overlay} onClick={onOverlayClick} />
        )}
        {renderedMap}
      </div>
    ),
    [renderedMap, overlayVisible, onOverlayClick],
  );
};
